import { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@material-ui/core';
import MuiDialogActions from '@material-ui/core/DialogActions';
import { Autocomplete, TextField, FormControl, Box } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_ALL_PAQUETES,
  CREATE_PACKAGE_CHANGE_REQUEST,
  GET_PACKAGE_CHANGE_PENDING_BY_COLABORADOR,
  GET_PENDING_PACKAGE_CHANGES,
} from '../../../Querys/prestaciones/querys';
import { SuccessfulAlert } from '../../../alerts/successAlerts';
import { ErrorAlert } from '../../../alerts/errorAlert';
import '../../Collaborator/History/History.css';

interface SolicitarCambioPaqueteModalProps {
  open: boolean;
  onClose: () => void;
  idColaborador: string;
  idProject?: string | number | null;
  idPaqueteActual?: string | null;
  nombrePaqueteActual?: string | null;
  idSolicitante: string;
}

const SolicitarCambioPaqueteModal = ({
  open,
  onClose,
  idColaborador,
  idProject,
  idPaqueteActual,
  nombrePaqueteActual,
  idSolicitante,
}: SolicitarCambioPaqueteModalProps) => {
  const [idPaqueteNuevo, setIdPaqueteNuevo] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const [touched, setTouched] = useState(false);

  const { data, loading } = useQuery(GET_ALL_PAQUETES, {
    fetchPolicy: 'no-cache',
    skip: !open,
  });

  const [createRequest, { loading: saving }] = useMutation(CREATE_PACKAGE_CHANGE_REQUEST, {
    refetchQueries: [
      { query: GET_PACKAGE_CHANGE_PENDING_BY_COLABORADOR, variables: { idColaborador } },
      { query: GET_PENDING_PACKAGE_CHANGES },
    ],
  });

  // Paquetes activos, excluyendo el actual; los del proyecto van primero como "Sugeridos".
  const opciones = useMemo(() => {
    const paquetes: any[] = data?.GET_ALL_PAQUETES ?? [];
    const activos = paquetes
      .filter((p: any) => p.status === true)
      .filter((p: any) => String(p.id) !== String(idPaqueteActual ?? ''));
    const sugeridos = activos
      .filter((p: any) => idProject && String(p.idProject) === String(idProject))
      .map((p: any) => ({ label: p.nombre, id: String(p.id), sugerido: true }));
    const otros = activos
      .filter((p: any) => !idProject || String(p.idProject) !== String(idProject))
      .map((p: any) => ({ label: p.nombre, id: String(p.id), sugerido: false }));
    return [...sugeridos, ...otros];
  }, [data, idProject, idPaqueteActual]);

  const motivoInvalido = motivo.trim() === '';
  const paqueteInvalido = !idPaqueteNuevo;

  const resetAndClose = () => {
    setIdPaqueteNuevo(null);
    setMotivo('');
    setTouched(false);
    onClose();
  };

  const handleSubmit = async () => {
    setTouched(true);
    if (motivoInvalido || paqueteInvalido) return;
    try {
      await createRequest({
        variables: {
          input: {
            idColaborador,
            idPaqueteNuevo,
            motivo: motivo.trim(),
            idSolicitante,
          },
        },
      });
      SuccessfulAlert({ text: 'Solicitud enviada. Quedó pendiente de aprobación.' });
      resetAndClose();
    } catch (e: any) {
      const msg = e?.message?.includes('PENDING_REQUEST_EXISTS')
        ? 'Este colaborador ya tiene una solicitud de cambio pendiente.'
        : 'Ocurrió un error al enviar la solicitud. Intenta de nuevo más tarde.';
      ErrorAlert({ text: msg });
    }
  };

  return (
    <Dialog
      aria-labelledby="solicitar-cambio-paquete-dialog"
      className="history-modal"
      open={open}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <h2 id="form-dialog-title" className="text-center" style={{ marginTop: 20 }}>
          Solicitar cambio de paquete
        </h2>
        <span id="closemodalx" onClick={resetAndClose}>
          <img alt="close" src="/assets/svg/CloseM.svg" />
        </span>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <CircularProgress style={{ color: '#FABB00' }} />
          </div>
        ) : (
          <>
            <Box mb={2} mt={1}>
              <TextField
                type="text"
                label="Paquete actual"
                value={nombrePaqueteActual ?? 'Sin paquete asignado'}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
                style={{ opacity: 0.6 }}
              />
            </Box>

            <Box mb={2}>
              <FormControl variant="outlined" fullWidth size="small">
                <Autocomplete
                  disablePortal
                  id="idPaqueteNuevo"
                  options={opciones}
                  groupBy={(option: any) => option.sugerido ? 'Sugeridos para este proyecto' : 'Otros paquetes'}
                  noOptionsText="No hay paquetes activos disponibles"
                  value={opciones.find((o) => o.id === idPaqueteNuevo) ?? null}
                  onChange={(_e, value: any) => setIdPaqueteNuevo(value?.id ?? null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      label="Nuevo paquete"
                      error={touched && paqueteInvalido}
                      helperText={touched && paqueteInvalido ? 'Selecciona un paquete' : ''}
                    />
                  )}
                />
              </FormControl>
            </Box>

            <Box mb={1}>
              <TextField
                label="Motivo"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                multiline
                minRows={3}
                error={touched && motivoInvalido}
                helperText={touched && motivoInvalido ? 'El motivo es obligatorio' : ''}
              />
            </Box>
          </>
        )}
      </DialogContent>

      <MuiDialogActions>
        <button className="btnCancel" onClick={resetAndClose} disabled={saving}>
          Cancelar
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving || loading}
          style={{
            backgroundColor: '#FABB00',
            color: '#222',
            border: 'none',
            borderRadius: 8,
            padding: '8px 20px',
            fontFamily: 'Roboto',
            fontWeight: 600,
            cursor: saving || loading ? 'default' : 'pointer',
            opacity: saving || loading ? 0.6 : 1,
          }}
        >
          {saving ? 'Enviando…' : 'Enviar solicitud'}
        </button>
      </MuiDialogActions>
    </Dialog>
  );
};

export default SolicitarCambioPaqueteModal;
